import {useTheme} from '@dropbox/dig-foundations';
import {isDev, isProd} from 'helpers/environment';
import {CSSProperties, SVGProps} from 'react';

import styles from './CompanyOSLogo.module.css';

interface CompanyOSLogoProps extends SVGProps<SVGSVGElement> {
  /** Render only the Dropbox glyph (no wordmark), for compact/mobile contexts. */
  mobile?: boolean;
  /** Badge the glyph with the DIG "AI" sparkle (used wherever we surface CompanyOS Ask). */
  ai?: boolean;
}

/**
 * Square 24-unit box around the Dropbox glyph with DIG-style padding, so the
 * standalone glyph reads at the same optical size as a DIG `UIIcon` (which has
 * built-in breathing room) rather than bleeding to the edges.
 */
const GLYPH_VIEWBOX = '6.14288 4 24 24';

/**
 * Tight box around the glyph (no padding) used by the loading spinner, whose
 * size is tuned by callers via `font-size`; padding here would shrink it.
 */
const GLYPH_VIEWBOX_TIGHT = '9.14288 7 18 18';

/**
 * Four-pointed "AI" sparkle matching DIG's AI icon badge (e.g. `AiChatFill`),
 * normalized so its center sits at the origin — place via `translate` + `scale`.
 */
const SPARKLE_PATH =
  'M-.3135-2.822a.366.366 0 0 1 .627 0 7.3 7.3 0 0 0 2.51 2.508.366.366 0 0 1 0 .628 7.3 7.3 0 0 0-2.51 2.508.366.366 0 0 1-.627 0 7.3 7.3 0 0 0-2.509-2.508.366.366 0 0 1 0-.628 7.3 7.3 0 0 0 2.51-2.508Z';
/** Center-to-tip radius of {@link SPARKLE_PATH} in its own units. */
const SPARKLE_BASE_RADIUS = 2.822;
/** Where the sparkle sits within the glyph's coordinate space (top-right corner). */
const SPARKLE_CENTER = {x: 24, y: 9.8};
const SPARKLE_RADIUS = 2.5;

const AiSparkle = ({color}: {color: string}): React.ReactElement => (
  <path
    d={SPARKLE_PATH}
    fill={color}
    transform={`translate(${SPARKLE_CENTER.x} ${SPARKLE_CENTER.y}) scale(${
      SPARKLE_RADIUS / SPARKLE_BASE_RADIUS
    })`}
  />
);

const useLogoColors = () => {
  const {mode} = useTheme();
  const isDark = mode === 'dark';
  return {
    glyphBack: isDark ? '#696663' : '#1A1918',
    blue: isDark ? '#3984FF' : '#0061FE',
    wordmark: isDark ? '#F7F5F2' : '#1E1919',
  };
};

// Visually distinguish non-production environments at a glance.
const useEnvFilterStyle = (style?: CSSProperties): CSSProperties | undefined => {
  const envFilter = isDev ? 'grayscale(1)' : !isProd ? 'hue-rotate(127deg)' : undefined;
  return envFilter ? {filter: envFilter, ...style} : style;
};

const Glyph = ({
  glyphBack,
  blue,
  backClassName,
  blueClassName,
  sparkle = false,
}: {
  glyphBack: string;
  blue: string;
  backClassName?: string;
  blueClassName?: string;
  /** Overlay the DIG "AI" sparkle in the glyph's top-right corner. */
  sparkle?: boolean;
}): React.ReactElement => (
  <>
    <path
      className={backClassName}
      d="M15.1769 13.0222H21.1274C21.0863 9.32632 19.0168 7.29236 15.2854 7.29236C11.5539 7.29236 9.44147 9.36745 9.44147 13.1363C9.44147 16.9051 11.4773 18.9372 15.1751 18.9783V13.0241H15.1769V13.0222Z"
      fill={glyphBack}
    />
    <path
      className={blueClassName}
      d="M21.1125 13.024V18.9745H15.1583C15.1994 22.6723 17.2689 24.7081 21.0003 24.7081C24.7317 24.7081 26.8442 22.633 26.8442 18.8642C26.8442 15.0954 24.8102 13.0652 21.1143 13.0222H21.1125V13.024Z"
      fill={blue}
    />
    {sparkle && <AiSparkle color={blue} />}
  </>
);

export const CompanyOSLogo = ({
  mobile = false,
  ai = false,
  style,
  ...props
}: CompanyOSLogoProps): React.ReactElement => {
  const {glyphBack, blue, wordmark} = useLogoColors();
  const mergedStyle = useEnvFilterStyle(style);

  if (mobile) {
    return (
      <svg width="36" height="32" viewBox="0 0 36 32" fill="none" style={mergedStyle} {...props}>
        <Glyph glyphBack={glyphBack} blue={blue} sparkle={ai} />
      </svg>
    );
  }

  return (
    <svg width="130" height="32" viewBox="0 0 130 32" fill="none" style={mergedStyle} {...props}>
      <Glyph glyphBack={glyphBack} blue={blue} sparkle={ai} />
      <path
        d="M32.7257 11.0662H37.0541C39.8221 11.0662 42.0826 12.6724 42.0826 16.3162V17.0949C42.0826 20.7522 39.9497 22.4073 37.1192 22.4073H32.7257V11.0662ZM35.1436 13.1201V20.3263H37.0053C38.5629 20.3263 39.6294 19.2925 39.6294 17.0189V16.4301C39.6294 14.1565 38.5331 13.1228 36.9238 13.1228H35.1436V13.1201ZM43.3689 13.2313H45.293L45.5942 15.3638C45.9606 13.9014 46.8995 13.1526 48.601 13.1526H49.1899V15.5863H48.2048C46.2319 15.5863 45.7543 16.2701 45.7543 18.21V22.41H43.3689V13.2313ZM49.7001 18.0173V17.7623C49.7001 14.6774 51.673 13.0224 54.346 13.0224C57.0814 13.0224 58.9756 14.7073 58.9756 17.7623V18.0173C58.9756 21.0398 57.114 22.646 54.346 22.646C51.388 22.646 49.7001 21.0398 49.7001 18.0173ZM56.5577 17.9848V17.7623C56.5577 16.0449 55.6974 15.0437 54.346 15.0437C53.0108 15.0437 52.118 15.9825 52.118 17.7623V17.9848C52.118 19.6398 52.962 20.5623 54.3297 20.5623C55.7001 20.5623 56.5577 19.6561 56.5577 17.9848ZM60.232 13.2313H62.2375L62.4763 14.9813C62.9702 13.8038 63.9878 13.0251 65.4994 13.0251C67.8386 13.0251 69.3664 14.71 69.3664 17.7976V18.0526C69.3664 21.0751 67.6649 22.6487 65.4994 22.6487C64.0367 22.6487 63.0652 21.9976 62.5713 20.9313V25.56H60.232V13.2313ZM66.9458 18.0011V17.7949C66.9458 15.9662 66.0069 15.06 64.7504 15.06C63.4153 15.06 62.5224 16.0774 62.5224 17.7786V17.9549C62.5224 19.5774 63.3827 20.5949 64.7016 20.5949C66.072 20.5949 66.9458 19.6887 66.9458 18.0011ZM72.8807 20.7387L72.6908 22.41H70.6229V10.16H72.9621V14.8673C73.4723 13.6735 74.49 13.0224 75.9364 13.0224C78.1155 13.0387 79.7383 14.5499 79.7383 17.6022V17.8735C79.7383 20.9286 78.2105 22.646 75.8713 22.646C74.3597 22.646 73.3746 21.9162 72.8807 20.7387ZM77.3367 17.8735V17.6511C77.3367 15.9662 76.414 15.0437 75.1413 15.0437C73.8197 15.0437 72.9133 16.11 72.9133 17.6836V17.8735C72.9133 19.591 73.7871 20.5786 75.1087 20.5786C76.4927 20.5786 77.3367 19.6887 77.3367 17.8735ZM80.8048 18.0173V17.7623C80.8048 14.6774 82.7614 13.0224 85.4344 13.0224C88.1699 13.0224 90.0803 14.7073 90.0803 17.7623V18.0173C90.0803 21.0398 88.2024 22.646 85.4344 22.646C82.4738 22.646 80.8048 21.0398 80.8048 18.0173ZM87.6597 17.9848V17.7623C87.6597 16.0449 86.7994 15.0437 85.448 15.0437C84.1128 15.0437 83.22 15.9825 83.22 17.7623V17.9848C83.22 19.6398 84.064 20.5623 85.4317 20.5623C86.8184 20.5623 87.6597 19.6561 87.6597 17.9848ZM93.1984 17.6511L89.9854 13.2286H92.7371L94.5987 15.9797L96.4766 13.2286H99.212L95.9501 17.6348L99.3857 22.4073H96.6801L94.5634 19.3685L92.5118 22.4073H89.7113L93.1984 17.6511Z"
        fill={wordmark}
      />
      <path
        d="M106.13 22.64C103.026 22.64 101.25 20.848 101.25 17.168V16.384C101.25 12.816 103.154 10.832 106.13 10.832C109.074 10.832 111.042 12.768 111.042 16.384V17.168C111.042 20.816 109.09 22.64 106.13 22.64ZM106.13 21.2C108.21 21.2 109.426 19.776 109.426 17.12V16.448C109.426 13.744 108.242 12.24 106.13 12.24C103.986 12.24 102.866 13.776 102.866 16.432V17.12C102.866 19.76 103.986 21.2 106.13 21.2ZM116.899 22.64C113.587 22.64 112.323 21.136 112.227 18.56H113.891C113.939 20.288 114.851 21.248 116.915 21.248C118.771 21.248 119.587 20.288 119.587 19.232C119.603 17.744 118.067 17.488 116.435 17.184C114.595 16.816 112.659 16.384 112.659 14C112.659 12.32 113.971 10.832 116.611 10.832C119.427 10.832 120.819 12.176 120.931 14.352H119.315C119.219 12.928 118.371 12.16 116.579 12.16C114.979 12.16 114.227 13.008 114.227 13.984C114.227 15.376 115.699 15.584 117.299 15.92C119.187 16.304 121.139 16.736 121.155 19.2C121.171 21.072 119.699 22.64 116.899 22.64Z"
        fill={blue}
      />
    </svg>
  );
};

/**
 * The Dropbox glyph only, sized to fill its box (1em) with a tight square viewBox.
 * Shaped to be used as a `UIIcon` / `AvatarIcon` `src`, where the wrapper controls size.
 */
interface CompanyOSGlyphProps extends SVGProps<SVGSVGElement> {
  /** Badge the glyph with the DIG "AI" sparkle (used wherever we surface CompanyOS Ask). */
  ai?: boolean;
}

export const CompanyOSGlyph = ({
  ai = false,
  style,
  ...props
}: CompanyOSGlyphProps): React.ReactElement => {
  const {glyphBack, blue} = useLogoColors();
  const mergedStyle = useEnvFilterStyle(style);

  return (
    <svg
      width="1em"
      height="1em"
      viewBox={GLYPH_VIEWBOX}
      fill="none"
      style={mergedStyle}
      {...props}
    >
      <Glyph glyphBack={glyphBack} blue={blue} sparkle={ai} />
    </svg>
  );
};

/**
 * {@link CompanyOSGlyph} pre-badged with the AI sparkle. Convenient where the glyph
 * is passed as a component reference (e.g. `UIIcon`/`AvatarIcon` `src`) and the `ai`
 * prop can't be set inline.
 */
export const CompanyAskGlyph = (props: SVGProps<SVGSVGElement>): React.ReactElement => (
  <CompanyOSGlyph ai {...props} />
);

/** Animation styles for {@link CompanyOSGlyphLoading}. */
export type CompanyOSGlyphLoadingVariant = 'converge' | 'pulse';

const LOADING_VARIANT_CLASSES: Record<
  CompanyOSGlyphLoadingVariant,
  {svg?: string; back?: string; blue?: string}
> = {
  // Spin + the two halves drift in to the center and back out.
  converge: {svg: styles.spinner, back: styles.convergeBack, blue: styles.convergeBlue},
  // Original: the two halves breathe (opacity + scale) out of phase.
  pulse: {back: styles.pulseBack, blue: styles.pulseBlue},
};

interface CompanyOSGlyphLoadingProps extends SVGProps<SVGSVGElement> {
  /** Animation style. Defaults to `converge`. */
  variant?: CompanyOSGlyphLoadingVariant;
}

/**
 * Animated Dropbox glyph used as a loading / thinking indicator. Sized to 1em
 * like {@link CompanyOSGlyph} so it can be dropped into a `UIIcon` / `AvatarIcon`
 * `src` (size via font-size/width). Respects `prefers-reduced-motion`.
 */
export const CompanyOSGlyphLoading = ({
  variant = 'converge',
  style,
  ...props
}: CompanyOSGlyphLoadingProps): React.ReactElement => {
  const {glyphBack, blue} = useLogoColors();
  const mergedStyle = useEnvFilterStyle(style);
  const variantClasses = LOADING_VARIANT_CLASSES[variant];

  return (
    <svg
      width="1em"
      height="1em"
      viewBox={GLYPH_VIEWBOX_TIGHT}
      fill="none"
      style={mergedStyle}
      role="img"
      aria-label="Loading"
      className={variantClasses.svg}
      {...props}
    >
      <Glyph
        glyphBack={glyphBack}
        blue={blue}
        backClassName={variantClasses.back}
        blueClassName={variantClasses.blue}
      />
    </svg>
  );
};
